import { getFormProps, useForm, useInputControl } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import {
	json,
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import { Link, useFetcher, useNavigate } from '@remix-run/react'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { getBudgetId, saveDefaultBudget } from '#app/utils/budget.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { BUDGET_PAYWALL_LIMIT } from '../budgets+/__budget-editor'

export const SelectBudgetFormSchema = z.object({
	budgetId: z.string(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	const userId = await requireUserId(request, { redirectTo: '/dashboard' })
	const budgetId = await getBudgetId(request)

	let where: any = {
		OR: [{ contributors: { some: { userId: userId } } }, { ownerId: userId }],
	}

	const budgets = await prisma.budget.findMany({
		where,
		select: {
			id: true,
			name: true,
			description: true,
			amount: true,
			currency: true,
			ownerId: true,
			type: true,
			owner: {
				select: { id: true, name: true },
			},
			contributors: {
				select: { id: true },
			},
		},
		orderBy: { createdAt: 'desc' },
	})

	const budgetsCreated = budgets
		.filter(b => b.ownerId === userId)
		.map(b => b.type)

	return json({
		selectedBudgetId: budgetId,
		canCreateBudget: budgetsCreated.length < BUDGET_PAYWALL_LIMIT,
		budgets: budgets.map(budget => ({
			...budget,
			isOwner: budget.ownerId === userId,
		})),
	})
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: SelectBudgetFormSchema,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { budgetId } = submission.value

	const responseInit = {
		headers: { 'set-cookie': await saveDefaultBudget(budgetId, request) },
	}
	return json({ result: submission.reply() }, responseInit)
}

export function BudgetSelect() {
	const budgetsFetcher = useFetcher<typeof loader>()
	const fetcherRef = useRef<typeof budgetsFetcher>()
	const budgets = budgetsFetcher.data?.budgets ?? []
	const budgetSelectedAction = useFetcher<typeof action>()
	const navigate = useNavigate()

	useEffect(() => {
		fetcherRef.current = budgetsFetcher
	}, [budgetsFetcher])

	useEffect(() => {
		fetcherRef.current?.load('/api/budgets')
	}, [fetcherRef])

	const [form, fields] = useForm({
		id: 'budget-select',
		lastResult: budgetSelectedAction.data?.result,
		constraint: getZodConstraint(SelectBudgetFormSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SelectBudgetFormSchema })
		},
	})

	const budgetInput = useInputControl(fields.budgetId)

	return (
		<>
			<budgetSelectedAction.Form
				method="POST"
				action="/api/budgets"
				{...getFormProps(form)}
				onChange={event => {
					budgetSelectedAction.submit(event.currentTarget, {
						preventScrollReset: true,
						navigate: false,
					})
				}}
			>
				<section>
					<div className="flex flex-row items-center space-x-2">
						{budgets?.length > 0 ? (
							<>
								<p className="text-body-xs font-bold uppercase tracking-wider">
									Presupuesto
								</p>
								<Select
									name={fields.budgetId.name}
									value={
										budgetInput.value ?? budgetsFetcher.data?.selectedBudgetId
									}
									onValueChange={value => {
										if (value === 'all-budgets') {
											navigate('/budgets')
											return
										}

										budgetInput.change(value)
									}}
								>
									<SelectTrigger className="rounded-none border-0 border-b-2 border-tertiary md:w-60">
										<SelectValue placeholder="Selecciona presupuesto" />
									</SelectTrigger>
									<SelectContent>
										{budgets.map(budget => (
											<SelectItem
												key={budget.id}
												value={budget.id}
												className="cursor-pointer"
											>
												<span className="text-body-sm">{budget.name}</span>
												{!budget.isOwner ? (
													<span className="ml-2 text-body-2xs font-bold uppercase tracking-wider text-tertiary">
														- Compartido
													</span>
												) : null}
											</SelectItem>
										))}
										<SelectItem
											value={'all-budgets'}
											className="cursor-pointer text-body-sm"
										>
											Ver Todos
										</SelectItem>
									</SelectContent>
								</Select>
								<Link
									to={`/budgets/${budgetsFetcher.data?.selectedBudgetId}/edit`}
								>
									<Button variant="ghost" size="icon" name="Editar Presupuesto">
										<Icon name="pencil-1" aria-hidden="true" />
									</Button>
								</Link>
								{budgetsFetcher.data?.canCreateBudget === true && (
									<Link to={`/budgets/new`}>
										<Button
											variant="ghost"
											size="icon"
											name="Agregar Presupuesto"
										>
											<Icon name="plus" aria-hidden="true" />
										</Button>
									</Link>
								)}
							</>
						) : budgets?.length ? (
							<>
								<p className="text-body-xs font-bold uppercase tracking-wider">
									Presupuesto
								</p>
								<div className="inline-flex h-10 w-10 items-center justify-center">
									<Icon name="update" className="animate-spin" />
								</div>
							</>
						) : null}
					</div>
				</section>
			</budgetSelectedAction.Form>
		</>
	)
}
